import React, { useState, useRef, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import styles from './EditableGoldStandard.module.css';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import GoldStandardSwitch from './GoldStandardSwitch';
import { Tooltip } from '@mui/material';

const EditableGoldStandard = ({ value, onUpdate, marginLeft, width, switchGoldStandard, globalGoldStandard }) => {
    const [localValue, setLocalValue] = useState(value);
    const [isEditing, setIsEditing] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [isHasError, setIsHasError] = useState(false);
    const [isHasUnsavedChanges, setIsHasUnsavedChanges] = useState(false);
    const [isGlobal, setIsGlobal] = useState(false);

    const textareaRef = useRef(null);
    const saveButtonRef = useRef(null);
    const publicUrl = process.env.PUBLIC_URL;

    const iconEditPath = publicUrl + '/images/icon-edit.svg';
    const iconSavePath = publicUrl + '/images/icon-save.svg';
    const iconWarningPath = publicUrl + '/images/icon-warning.svg';
    const iconUndoPath = publicUrl + '/images/icon-undo.svg';
    const missingValue = 'Missing';

    useEffect(() => {
        setLocalValue(value || missingValue);
        setIsGlobal(globalGoldStandard && value === globalGoldStandard);
        setIsDirty(false);
        setIsHasUnsavedChanges(false);
    }, [value, globalGoldStandard]);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            const maxHeight = 7 * 24;
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, maxHeight) + 'px';
        }
    }, [localValue]);

    const handleChange = (e) => {
        const newValue = e.target.value;

        setLocalValue(newValue);
        setIsDirty(newValue !== value);
    };

    const handleSave = async () => {
        const savedValue = localValue.trim() ? localValue : missingValue;

        try {
            await onUpdate(savedValue);
            setLocalValue(savedValue);
            setIsHasError(false);
            setIsHasUnsavedChanges(false);
        } catch (err) {
            setIsHasError(true);
            setIsHasUnsavedChanges(true);
        } finally {
            setIsEditing(false);
            setIsDirty(false);
        }
    };

    const handleClearClick = () => {
        setLocalValue(value);
        setIsDirty(false);
        setIsHasUnsavedChanges(false);
    };

    const handleEditClick = () => {
        setIsEditing(true);

        if (!isGlobal) setIsGlobal(true);
        if (isHasUnsavedChanges) setIsHasUnsavedChanges(false);
        if (isHasError) setIsHasError(false);
        if (localValue === missingValue) setLocalValue('');

        textareaRef.current?.focus();
    };

    const handleBlur = (e) => {
        if (e.relatedTarget && e.relatedTarget === saveButtonRef.current) return;

        if (!localValue || !localValue.trim().length) {
            setLocalValue(missingValue);
            if (value && value !== missingValue) setIsHasUnsavedChanges(true);
        } else if (isDirty) {
            setIsHasUnsavedChanges(true);
        }

        setIsEditing(false);
    };

    const handleSwitchChange = () => {
        setIsGlobal((prev) => !prev);
        switchGoldStandard();
    };

    return (
        <div className={styles.goldStandard} style={{ width: '100%' }}>
            <div className={styles.goldStandardTitleContainer}>
                <p>Gold Standard </p>
                <GoldStandardSwitch checked={isGlobal} onChange={handleSwitchChange}/>
            </div>
            <TextareaAutosize
                maxRows={7}
                ref={textareaRef}
                name='goldValue'
                className={styles.goldStandardValue}
                style={{
                    marginLeft,
                    width,
                    color: (
                        localValue === missingValue
                            ? '#C8C8C8'
                            : (isHasUnsavedChanges
                                ? 'yellow'
                                : 'inherit')
                        ),
                    resize: 'none',
                    overflow: 'hidden',
                    borderBottom: isEditing && '2px solid #FFFFFF'

                }}
                value={localValue}
                onChange={handleChange}
                onBlur={handleBlur}
                onClick={handleEditClick}
            />
            {(isEditing && isDirty) || isHasUnsavedChanges ? (
                <Tooltip
                    title='Save gold standard'
                    placement="top"
                    slotProps={{
                        popper: {
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: { offset: [0, -14] },
                                },
                            ],
                        },
                    }}
                >
                    <IconButton
                        className={styles.goldStandardButton}
                        ref={saveButtonRef}
                        onClick={handleSave}
                    >
                        <img src={iconSavePath} alt="Save icon" />
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip
                    title='Edit gold standard'
                    placement="top"
                    slotProps={{
                        popper: {
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: { offset: [0, -14] },
                                },
                            ],
                        },
                    }}
                >
                    <IconButton
                        className={styles.goldStandardButton}
                        onClick={handleEditClick}
                    >
                        <img src={iconEditPath} alt="Edit icon" />
                    </IconButton>
                </Tooltip>
            )}
            {(isHasError || isHasUnsavedChanges) && (
                <Tooltip
                    title={isHasError ? 'Failed to update gold standard' : 'Your changes are not saved'}
                    placement="top"
                    slotProps={{
                        popper: {
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: { offset: [0, -14] },
                                },
                            ],
                        },
                    }}
                >
                    <IconButton
                        className={styles.goldStandardButton}
                        style={{ right: '40px' }}
                    >
                        <img src={iconWarningPath} alt="Warning icon" />
                    </IconButton>
                </Tooltip>
            )}
            {(isEditing && localValue !== value) && (
                <Tooltip
                    title='Reset to initial value'
                    placement="top"
                    slotProps={{
                        popper: {
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: { offset: [0, -14] },
                                },
                            ],
                        },
                    }}
                >
                    <IconButton
                        className={styles.goldStandardButton}
                        style={{ right: '40px' }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleClearClick}
                    >
                        <img src={iconUndoPath} alt="Undo icon" />
                    </IconButton>
                </Tooltip>
            )}
        </div>
    );
};

export default EditableGoldStandard;
